import {
  Box,
  Button,
  Card,
  Flex,
  Image,
  InfoIcon,
  Popover,
  RadioFieldGroup,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';

import type { BloomFooterTemplateFormState } from '@jane/business-admin/types';
import {
  MAX_IMAGE_SIZE,
  VALID_IMAGE_MIME_TYPES,
} from '@jane/business-admin/util';
import { ImagePreview } from '@jane/shared/util';

import styles from './Footer.module.css';
import { ImageWireframe } from './FooterWireframes';

type ImageOption = 'logo' | 'upload' | 'none';

export const ImageSection = ({ logoUrl }: { logoUrl: string }) => {
  const { control, setValue, watch, trigger } =
    useFormContext<BloomFooterTemplateFormState>();
  const imageUrl = watch('image');
  const useLogo = watch('use_logo');

  const [imageError, setImageError] = useState<string>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(
    imageUrl ?? null
  );
  const [imageOption, setImageOption] = useState<ImageOption>('none');
  const [hasInitialized, setHasInitialized] = useState(false);

  const onDrop = useCallback(
    async ([file]: File[]) => {
      if (
        file.size >= MAX_IMAGE_SIZE ||
        !VALID_IMAGE_MIME_TYPES.includes(file.type)
      ) {
        setImageError('Image file must be PNG or JPG - Max 2MB');
      } else {
        const image = await ImagePreview.parse(file);
        setUploadedImageUrl(image.url);
        setValue('use_logo', false, { shouldDirty: true });
        setValue('image', image.url, { shouldDirty: true });
        setImageOption('upload');
        setImageError('');
      }
    },
    [setValue]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
  });

  // Initialize radio selection when data is available
  useEffect(() => {
    if (!hasInitialized) {
      if (useLogo) {
        // Use logo - don't store logo URL as uploaded image
        setImageOption('logo');
        setUploadedImageUrl(null);
      } else if (imageUrl) {
        // It's an uploaded image object from API (not logo)
        setImageOption('upload');
        setUploadedImageUrl(imageUrl);
      } else if (!useLogo) {
        setImageOption('none');
        setUploadedImageUrl(null);
      }
      setHasInitialized(true);
    }
  }, [useLogo, imageUrl, hasInitialized]);

  // Trigger validation to disable publishing if image/radio selection is invalid
  useEffect(() => {
    if (hasInitialized) {
      trigger('image');
    }
  }, [imageOption, trigger, hasInitialized, uploadedImageUrl]);

  const handleOptionChange = async (option: ImageOption) => {
    setImageOption(option);

    switch (option) {
      case 'logo':
        setValue('use_logo', true, { shouldDirty: true });
        setValue('image', null, { shouldDirty: true });
        break;
      case 'upload':
        setValue('use_logo', false, { shouldDirty: true });

        if (uploadedImageUrl) {
          setValue('image', uploadedImageUrl, { shouldDirty: true });
        } else {
          setValue('image', null, { shouldDirty: true });
        }
        break;
      case 'none':
        setValue('use_logo', false, { shouldDirty: true });
        setValue('image', null, { shouldDirty: true });
        break;
    }
  };

  const handleRemoveUploadedImage = (e: MouseEvent) => {
    e.stopPropagation();
    setUploadedImageUrl(null);
    setValue('use_logo', false, { shouldDirty: true });
    setValue('image', null, { shouldDirty: true });
    setImageError('');
  };

  return (
    <Card
      border="grays-ultralight"
      className={styles['sectionCard']}
      id="image"
    >
      <Flex flexDirection="column" gap={16} p={40}>
        <Typography variant="header-bold">Image</Typography>
        <Typography>
          You can use your saved store logo, upload an image, or choose not to
          use an image at all.
        </Typography>

        <div className={styles['imageOptionsContainer']}>
          <RadioFieldGroup
            name="image_option"
            externalValue={imageOption}
            onChange={handleOptionChange}
            options={[
              {
                id: 'logo',
                label: 'Use saved logo',
                value: 'logo',
                wrapper: (children) => (
                  <Flex
                    flexDirection="column"
                    gap={16}
                    width="100%"
                    ariaLabel="Use logo"
                    onClick={() => handleOptionChange('logo')}
                  >
                    {children}
                    <Box
                      height={264}
                      width="100%"
                      borderRadius="sm"
                      borderWidth="2px"
                      border="grays-light"
                      className={styles['imageContainer']}
                    >
                      <Image
                        key={logoUrl}
                        src={logoUrl}
                        height="100%"
                        width="100%"
                        altText="Store Logo"
                        borderRadius="8px"
                        objectFit="contain"
                      />
                    </Box>
                  </Flex>
                ),
              },
              {
                id: 'upload',
                label: 'Upload a different image',
                value: 'upload',
                wrapper: (children) => (
                  <Flex
                    flexDirection="column"
                    gap={16}
                    width="100%"
                    ariaLabel="Use a different image"
                    onClick={() => handleOptionChange('upload')}
                  >
                    {children}
                    <div {...getRootProps()}>
                      <Box
                        p={16}
                        borderRadius="sm"
                        height={264}
                        width="100%"
                        border={imageError ? 'error' : 'grays-light'}
                        borderWidth="2px"
                        className={
                          uploadedImageUrl
                            ? styles['imageContainer']
                            : styles['imageUploadContainer']
                        }
                      >
                        <Flex
                          flexDirection="column"
                          alignItems="center"
                          alignContent="center"
                          height="100%"
                          justifyContent="center"
                          position="relative"
                        >
                          {uploadedImageUrl ? (
                            <>
                              <Image
                                key={uploadedImageUrl}
                                src={uploadedImageUrl}
                                height="100%"
                                width="100%"
                                altText="Uploaded Image"
                                borderRadius="8px"
                                objectFit="contain"
                              />
                              <Box
                                position="absolute"
                                top={0}
                                right={0}
                                style={{ zIndex: 1 }}
                              >
                                <Button.Icon
                                  ariaLabel="Remove uploaded image"
                                  onClick={handleRemoveUploadedImage}
                                  variant="secondary"
                                  icon={<TrashIcon />}
                                />
                              </Box>
                            </>
                          ) : (
                            <>
                              <Button
                                mb={8}
                                label="Upload image"
                                type="button"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  open();
                                }}
                              />
                              <input {...getInputProps()} />
                              <Typography
                                color={imageError ? 'error' : 'grays-mid'}
                                variant="body"
                              >
                                {imageError
                                  ? imageError
                                  : 'PNG or JPG - Max 2MB'}
                              </Typography>
                              <Typography color="grays-mid" variant="body">
                                or
                              </Typography>
                              <Typography color="grays-mid" variant="body">
                                Drag and drop image here
                              </Typography>
                            </>
                          )}
                        </Flex>
                      </Box>
                    </div>
                  </Flex>
                ),
              },
              {
                id: 'none',
                label: 'No image in the footer',
                value: 'none',
                wrapper: (children) => (
                  <Flex
                    gap={16}
                    alignItems="center"
                    ariaLabel="No image"
                    onClick={() => handleOptionChange('none')}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    {children}
                    <Popover
                      target={<InfoIcon size="sm" />}
                      label="More info"
                      openOn="hover"
                      alignment={{ horizontal: 'left', vertical: 'top' }}
                    >
                      <Popover.Content>
                        <Flex
                          flexDirection="column"
                          gap={8}
                          alignItems="center"
                        >
                          <Typography variant="body-bold">
                            Don't want an image?
                          </Typography>
                          <Typography textAlign="center">
                            If you choose not to use an image, the
                            call-to-action and blurb will just move up!
                          </Typography>
                          <ImageWireframe />
                        </Flex>
                      </Popover.Content>
                    </Popover>
                  </Flex>
                ),
              },
            ]}
          />
        </div>

        {/* Hidden inputs for image and use_logo */}
        <Controller
          control={control}
          name="image"
          rules={{
            validate: (value) => {
              if (imageOption === 'upload' && !value) {
                return 'Please upload an image or select a different option';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <input type="hidden" {...field} value={field.value ?? ''} />
          )}
        />
        <Controller
          control={control}
          name="use_logo"
          render={({ field }) => (
            <input
              type="hidden"
              {...field}
              value={field.value ? 'true' : 'false'}
            />
          )}
        />
      </Flex>
    </Card>
  );
};
